import {Box, createTheme, CssBaseline, Stack, ThemeProvider, useMediaQuery} from "@mui/material";
import {Route, BrowserRouter as Router, Routes} from "react-router";
import "@fontsource/poppins";
import {useContext, useEffect, useState} from "react";
import axios from "axios";
import {TokenContext} from "../Context/TokenContext";
import {UserContext} from "../Context/UserContext";
import PermissionGate from "./PermissionGate";
import MainPage from "../Page/MainPage";
import Programs from "../Page/programs";
import ClientsPage from "../Page/ClientsPage";
import OrdersPage from "../Page/OrdersPage";
import ApplicationsPage from "../Page/ApplicationsPage";
import TicketsPage from "../Page/TicketsPage";
import DocumentationPage from "../Page/DocumentationPage";
import ApplicationManagementPage from "../Page/ApplicationManagementPage";
import ClientManagementPage from "../Page/ClientManagementPage";
import SupportSessionPage from "../Page/SupportSessionPage";
import SupportHistoryPage from "../Page/SupportHistoryPage";
import PricingManagementPage from "../Page/PricingManagementPage";
import CategoriesListPage from "../Page/CategoriesListPage";
import CategoryFormPage from "../Page/CategoryFormPage";
import ClientGroupsListPage from "../Page/ClientGroupsListPage";
import ClientGroupFormPage from "../Page/ClientGroupFormPage";
import PricingPlansListPage from "../Page/PricingPlansListPage";
import ServicePackagesPage from "../Page/ServicePackagesPage";
import DemoSitesPage from "../Page/DemoSitesPage";
import QuotesPage from "../Page/QuotesPage";
import CreateQuotePage from "../Page/CreateQuotePage";
import SettingsPage from "../Page/SettingsPage";
import LegalNoticeSettingsPage from "../Page/LegalNoticeSettingsPage";
import CGVSettingsPage from "../Page/CGVSettingsPage";
import CGUListPage from "../Page/CGUListPage";
import CGUEditPage from "../Page/CGUEditPage";
import AdministrateursPage from "../Page/AdministrateursPage";
import ProspectsPage from "../Page/ProspectsPage";
import EmployesAdminPage from "../Page/EmployesAdminPage";
import GroupesPage from "../Page/GroupesPage";
import ProtectedRoute from "./ProtectedRoute";
import SideBar from "./sidebar/Sidebar";

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

function Main() {

    // const prefersDarkMode = false;
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const isComputer = useMediaQuery('(min-width:900px)');
    const [sidebarIsOpen, setSidebarIsOpen] = useState();

    const {accessToken} = useContext(TokenContext);
    const {setUserInfo} = useContext(UserContext);

    useEffect(() => {
        if (!accessToken) return;
        axios.get(`${API_URL}/api/account_info`, {headers: {Authorization: `Bearer ${accessToken}`}})
            .then(r => setUserInfo(r.data))
            .catch(() => {});
    }, [accessToken, setUserInfo]);
    const darkTheme = createTheme({
        palette: {
            mode: prefersDarkMode ? 'dark' : 'light',
            background: {
                default: prefersDarkMode ? '#1A1A1A' : '#f5f5f5',
            },
            primary: {
                main: '#ca0000',
            },
            appBackground: {
                main: '#f5f5f5',
                light: '#f5f5f5',
                dark: '#1A1A1A'
            },
            sidebar: {
                dark: '#cecece',
                light: '#616161',
                main: '#616161'
            },
            neutralButton: {
                main: '#1e1e1e',
                light: '#ca0000',
                dark: '#1e1e1e',
            }
        },
        typography: {
            fontFamily: '"Poppins", sans-serif',
            allVariants: {
                color: prefersDarkMode ? '#cecece' : '#000000FF',
            }
        },
    });


    const getContainerClass = () => {
        let classes = []
        if (!isComputer) {
            classes.push("mobile")
        } else {
            const animation = sidebarIsOpen ? 'activated' : 'disactivated'
            classes.push(animation)
        }

        return classes.join(' ')
    }

    return (
            <ThemeProvider theme={darkTheme}>
                <ProtectedRoute>
                    <SideBar sidebarIsOpen={sidebarIsOpen} setSidebarIsOpen={setSidebarIsOpen} isComputer={isComputer}/>
                    <CssBaseline/>
                    <Stack
                        direction="column"
                        spacing={2}
                        sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: '100%'
                        }}
                        minHeight={'100vh'}
                    >
                        <Box width={'100%'} id='Md-Container'  className={getContainerClass()}>
                            <Routes>
                                <Route exact path="/" element={<MainPage/>}/>
                                <Route exact path="/programs" element={<Programs/>}/>
                                <Route exact path="/prospects" element={<PermissionGate section="prospects"><ProspectsPage/></PermissionGate>}/>
                                <Route exact path="/employes" element={<PermissionGate section="employes"><EmployesAdminPage/></PermissionGate>}/>
                                <Route exact path="/groupes" element={<PermissionGate section="manage-own-group-members"><GroupesPage/></PermissionGate>}/>
                                <Route exact path="/clients" element={<PermissionGate section="clients"><ClientsPage/></PermissionGate>}/>
                                <Route exact path="/orders" element={<PermissionGate section="orders"><OrdersPage/></PermissionGate>}/>
                                <Route exact path="/clients/:id" element={<PermissionGate section="clients"><ClientManagementPage/></PermissionGate>}/>
                                <Route exact path="/clients/:id/support/:sessionToken" element={<PermissionGate section="clients"><SupportSessionPage/></PermissionGate>}/>
                                <Route exact path="/support/historique" element={<PermissionGate section="support.view_history"><SupportHistoryPage/></PermissionGate>}/>
                                <Route exact path="/applications" element={<PermissionGate section="applications"><ApplicationsPage/></PermissionGate>}/>
                                <Route exact path="/applications/:id" element={<PermissionGate section="applications"><ApplicationManagementPage/></PermissionGate>}/>
                                <Route exact path="/applications/:id/pricing" element={<PermissionGate section="applications"><PricingManagementPage/></PermissionGate>}/>
                                <Route exact path="/tickets" element={<PermissionGate section="tickets"><TicketsPage/></PermissionGate>}/>
                                <Route exact path="/documentation" element={<DocumentationPage/>}/>
                                <Route exact path="/categories" element={<PermissionGate section="categories"><CategoriesListPage/></PermissionGate>}/>
                                <Route exact path="/categories/new" element={<PermissionGate section="categories"><CategoryFormPage/></PermissionGate>}/>
                                <Route exact path="/categories/:id/edit" element={<PermissionGate section="categories"><CategoryFormPage/></PermissionGate>}/>
                                <Route exact path="/client-groups" element={<PermissionGate section="client-groups"><ClientGroupsListPage/></PermissionGate>}/>
                                <Route exact path="/client-groups/new" element={<PermissionGate section="client-groups"><ClientGroupFormPage/></PermissionGate>}/>
                                <Route exact path="/client-groups/:id/edit" element={<PermissionGate section="client-groups"><ClientGroupFormPage/></PermissionGate>}/>
                                <Route exact path="/pricing-plans" element={<PermissionGate section="pricing-plans"><PricingPlansListPage/></PermissionGate>}/>
                                <Route exact path="/service-packages" element={<PermissionGate section="service-packages"><ServicePackagesPage/></PermissionGate>}/>
                                <Route exact path="/demo-sites" element={<PermissionGate section="demo-sites"><DemoSitesPage/></PermissionGate>}/>
                                <Route exact path="/quotes" element={<PermissionGate section="quotes"><QuotesPage/></PermissionGate>}/>
                                <Route exact path="/quotes/new" element={<PermissionGate section="quotes"><CreateQuotePage/></PermissionGate>}/>
                                <Route exact path="/settings" element={<PermissionGate section="settings"><SettingsPage/></PermissionGate>}/>
                                <Route exact path="/settings/mentions-legales" element={<PermissionGate section="settings"><LegalNoticeSettingsPage/></PermissionGate>}/>
                                <Route exact path="/settings/cgv" element={<PermissionGate section="settings"><CGVSettingsPage/></PermissionGate>}/>
                                <Route exact path="/settings/cgu" element={<PermissionGate section="settings"><CGUListPage/></PermissionGate>}/>
                                <Route exact path="/settings/cgu/:clientId" element={<PermissionGate section="settings"><CGUEditPage/></PermissionGate>}/>
                                <Route exact path="/administrateurs" element={<PermissionGate requireSuperAdmin><AdministrateursPage/></PermissionGate>}/>
                            </Routes>

                        </Box>
                    </Stack>
                </ProtectedRoute>
            </ThemeProvider>

    );
}

export default Main;
